import styled from '@emotion/styled'
import { DOWNLOAD_API } from 'api/download-api'
import {
  AssetInformationModal,
  DownloadLink,
  onDownloadLinkClick,
  Rule,
  Tabs
} from 'components/core-v2'
import { Loading } from 'components/core-v2/pages'
import { useState } from 'react'

import { AssetConcentrationPage } from '../pages/asset-concentration-page'
import { AssetLookThroughPage } from '../pages/asset-look-through-page'
import { MarketDataPage } from '../pages/market-data-page'
import { MaximumDrawdownPage } from '../pages/maximum-drawdown-page'
import { ScenarioPage } from '../pages/scenario-page'
import { actions as rawActions } from '../state/gap-risk-slice'
import { useAppSelector } from '../store/hooks'
import { useActions } from '../store/use-actions'

export type GapRiskLayoutProps = {
  isLoading: boolean
}

const StyledFixScrollingArea = styled.div`
  overflow-y: auto;
  background: var(--White98);
  height: 100%;
  flex: 1;
  padding: 36px 24px 120px;
  * {
    font-size: 15px !important;
  }
`

export type TabName =
  | 'market-data'
  | 'scenario'
  | 'maximum-drawdown'
  | 'asset-concentration'
  | 'asset-look-through'

export const GapRiskLayout = ({ isLoading }: GapRiskLayoutProps) => {
  const state = useAppSelector(x => x.gapRisk)
  const actions = useActions(rawActions)
  const [selectedTab, setSelectedTab] = useState<TabName>('market-data')

  return isLoading ? (
    <Loading className="mt-12" />
  ) : (
    <>
      <div className="d-flex">
        <Tabs.List className="mt-4 mx-3" showRule={false} spacing="md">
          <Tabs.Tab
            isSelected={selectedTab === 'market-data'}
            value="market-data"
            onClick={() => setSelectedTab('market-data')}
          >
            Market data
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={selectedTab === 'scenario'}
            value="scenario"
            onClick={() => setSelectedTab('scenario')}
          >
            Scenario analysis
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={selectedTab === 'maximum-drawdown'}
            value="maximum-drawdown"
            onClick={() => setSelectedTab('maximum-drawdown')}
          >
            Maximum drawdown
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={selectedTab === 'asset-concentration'}
            value="asset-concentration"
            onClick={() => setSelectedTab('asset-concentration')}
          >
            Asset concentration
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={selectedTab === 'asset-look-through'}
            value="asset-look-through"
            onClick={() => setSelectedTab('asset-look-through')}
          >
            Asset look through
          </Tabs.Tab>
        </Tabs.List>
        <DownloadLink
          className="ml-auto mr-4"
          onClick={() =>
            onDownloadLinkClick({
              url: DOWNLOAD_API.exportRiskReport
            })
          }
        >
          Risk report
        </DownloadLink>
        <DownloadLink
          className="mr-10"
          onClick={() =>
            onDownloadLinkClick({
              url: DOWNLOAD_API.exportHoldingsReport
            })
          }
        >
          Holdings report
        </DownloadLink>
      </div>

      <Rule />

      <StyledFixScrollingArea>
        {selectedTab === 'market-data' && (
          <Tabs.Panel value="market-data">
            <MarketDataPage />
          </Tabs.Panel>
        )}
        {selectedTab === 'scenario' && (
          <Tabs.Panel value="scenario">
            <ScenarioPage />
          </Tabs.Panel>
        )}
        {selectedTab === 'maximum-drawdown' && (
          <Tabs.Panel value="maximum-drawdown">
            <MaximumDrawdownPage />
          </Tabs.Panel>
        )}
        {selectedTab === 'asset-concentration' && (
          <Tabs.Panel value="asset-concentration">
            <AssetConcentrationPage />
          </Tabs.Panel>
        )}
        {selectedTab === 'asset-look-through' && (
          <Tabs.Panel value="asset-look-through">
            <AssetLookThroughPage />
          </Tabs.Panel>
        )}
        <AssetInformationModal
          assetId={state.assetBeingInspected?.assetId}
          isOpen={state.isInspectingAsset}
          onClose={() => actions.stopInspectingAsset()}
        />
      </StyledFixScrollingArea>
    </>
  )
}
